import { observer } from "mobx-react-lite";
import { useFormatter, useTranslations } from "next-intl";

import { twMerge } from "@/utils/twMerge";

import { BillingCard, BillingSection } from "./BillingSection";
import { getPeriodEnd } from "./periodEnd";

import type { TeamBillingStore } from "@/store/team/TeamBillingStore";

export const Credits = observer(function Credits({ billing }: { billing: TeamBillingStore }) {
  const t = useTranslations();
  const format = useFormatter();
  const { subscription, creditPools } = billing;
  const plan = subscription?.subscriptionPlan;
  const { subscription: subscriptionPool, personalFree: freePool } = creditPools;

  const pools = [subscriptionPool, freePool].flatMap((pool) => pool ?? []);
  if (pools.length === 0) return null;

  const granted = pools.reduce((sum, pool) => sum + pool.granted, 0);
  const used = pools.reduce((sum, pool) => sum + pool.used, 0);
  const usedPercent = granted > 0 ? (used / granted) * 100 : 0;
  const isSpent = used >= granted;

  const periodEnd = subscriptionPool && subscription && getPeriodEnd(subscription, format);
  const title =
    subscriptionPool && plan
      ? t("teamDialog.billing.credits.subscription", { plan: plan.name })
      : t("teamDialog.billing.credits.free");
  const caption = [
    plan?.kind === "team" &&
      subscriptionPool &&
      t("teamDialog.billing.credits.perMember", { credits: subscriptionPool.granted }),
    periodEnd &&
      t(periodEnd.isEnding ? "teamDialog.billing.ends" : "teamDialog.billing.renews", {
        date: periodEnd.day,
      }),
    !subscriptionPool &&
      freePool?.expiresAt &&
      t("teamDialog.billing.credits.resets", {
        time: format.relativeTime(new Date(freePool.expiresAt), new Date()),
      }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <BillingSection title={t("teamDialog.billing.credits.title")}>
      <BillingCard className="flex flex-col gap-1.5 p-2.5">
        <div className="flex justify-between">
          <span className="font-semibold">{title}</span>
          <span className="text-gray-500">
            {t("teamDialog.billing.credits.used", { used, granted })}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label={title}
          aria-valuemin={0}
          aria-valuemax={granted}
          aria-valuenow={used}
          className="h-1 rounded-full bg-gray-200 dark:bg-gray-700"
        >
          <div
            className={twMerge("h-full rounded-full bg-blue-500", isSpent && "bg-red-400")}
            style={{ width: `${usedPercent}%` }}
          />
        </div>
        {caption && <div className="text-gray-500 text-[14px]">{caption}</div>}
      </BillingCard>
    </BillingSection>
  );
});
